import { media, font } from "../../theme";
import styled from "styled-components";
import Content, { DEFAULT_PADDING } from "../../common/Content";
import { MOBILE_HEIGHT, HEIGHT } from "../../Header/constants";
import { slug } from "../../../lib/formatters";
import { SectionWithNavLink } from ".";
import A from "../../common/A";

const Nav = styled.nav<{ scrollable: boolean }>`
  position: sticky;
  margin: 0;
  top: ${HEIGHT}px;
  max-width: 200px;
  @media print {
    display: none;
  }
  @media screen and ${media.tablet} {
    top: ${MOBILE_HEIGHT}px;
  }
  @media screen and ${media.mobile} {
    max-width: none;
    top: ${MOBILE_HEIGHT}px;
    background: #fff;
    box-shadow: 0 20px 15px -10px rgba(0, 69, 106, 0.08);
    z-index: 2;
    display: flex;
    flex-direction: row;
  }

  -ms-overflow-style: ${p => (p.scrollable ? "none" : "")};
  max-height: ${p => (p.scrollable ? "calc(100vh - 9rem)" : "")};
  overflow-y: ${p => (p.scrollable ? "auto" : "")};
`;
const StyledContent = styled(Content)`
  @media screen and ${media.mobile} {
    position: sticky;
    top: ${MOBILE_HEIGHT}px;
    z-index: 3;
  }
`;
const Absolute = styled.aside`
  position: absolute;
  z-index: 3;
  top: 0;
  height: 100%;
  width: auto;
  @media screen and ${media.mobile} {
    position: sticky;
    top: ${MOBILE_HEIGHT}px;
    padding-top: 0;
    margin-left: -${DEFAULT_PADDING};
    width: 100vw;
  }
  /* Target IE11 specifically  */
  @media all and (-ms-high-contrast: none) and ${media.mobile} {
    position: relative;
    top: 0;
  }
`;
const NavItem = styled(A)`
  font-weight: ${font.weightMedium};
  display: block;
  margin-bottom: calc(4px * 5);
  @media screen and ${media.mobile} {
    margin: 0;
    width: auto;
    flex-shrink: 0;
  }
`;

const NavList = styled.ul`
  margin: 0;
  padding: 48px 1.5em 24px 0;

  @media screen and ${media.mobile} {
    display: flex;
    flex-direction: row;
    padding: calc(4px * 4) ${DEFAULT_PADDING};
    overflow-x: auto;
  }
`;

const NavListItem = styled.li`
  list-style: none;
  @media screen and ${media.mobile} {
    flex-shrink: 0;
    &:not(:first-child) {
      margin-left: calc(4px * 5);
    }
    &:last-child {
      padding-right: ${DEFAULT_PADDING};
    }
  }
`;

export const shortenNavLink = (s: string): string => s;

export const extractLinksFromSections = (
  sections: SectionWithNavLink[]
): NavLinkAndId[] => {
  if (!sections || !sections.length) return [];
  return sections.reduce((result, { navLink, id }) => {
    if (navLink) {
      result.push({
        id: id || slug(shortenNavLink(navLink)),
        navLink
      });
    }
    return result;
  }, []);
};

export interface NavLinkAndId {
  navLink: string;
  id: string;
}
interface Props {
  links: NavLinkAndId[];
  withSectionListWithNav?: boolean;
  className?: string;
  scrollable?: boolean;
}
export const StickyNav: React.FC<Props> = ({
  links,
  withSectionListWithNav,
  className,
  scrollable
}) => {
  const Inner = (
    <Absolute
      className={withSectionListWithNav ? className : ""}
      aria-label="Sprungmarken"
    >
      <Nav scrollable={scrollable}>
        {links && (
          <NavList>
            {links.map(
              (link, idx) =>
                link.navLink && (
                  <NavListItem key={idx}>
                    <NavItem
                      key={idx}
                      href={`#${link.id}`}
                      tabIndex={0}
                      aria-label={`Springe zu ${link.navLink}`}
                    >
                      {shortenNavLink(link.navLink)}
                    </NavItem>
                  </NavListItem>
                )
            )}
          </NavList>
        )}
      </Nav>
    </Absolute>
  );
  return withSectionListWithNav ? (
    Inner
  ) : (
    <StyledContent padded className={withSectionListWithNav ? className : ""}>
      {Inner}
    </StyledContent>
  );
};
